import { buildChildrenByParent } from "../components/ChatbotFlow/chatbotTreeModel";
import { buildSlotPath, slotStepName } from "./chatbotNavigationActions";

/** Separadores usados em caminhos de passo (` › ` na API, `>` em algumas telas). */
const PATH_SEGMENT_SPLIT = /\s*(?:›|»|>)\s*/;

export function parsePathSegments(pathString) {
  if (!pathString || typeof pathString !== "string") return [];
  return pathString
    .split(PATH_SEGMENT_SPLIT)
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeLabel(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function segmentMatchesSlot(slot, segmentLabel) {
  if (!slot) return false;
  const seg = normalizeLabel(segmentLabel);
  if (!seg) return false;

  const stepName = slotStepName(slot);
  if (normalizeLabel(stepName) === seg) return true;

  const id = String(slot.identificador || "").trim();
  const nome = String(slot.nome || "").trim();
  if (id && normalizeLabel(id) === seg) return true;
  if (nome && normalizeLabel(nome) === seg) return true;
  if (id && nome && normalizeLabel(`${id} — ${nome}`) === seg) return true;
  if (id && nome && normalizeLabel(`${id} - ${nome}`) === seg) return true;

  return false;
}

function findSlotBySegmentLabel(candidates, segmentLabel) {
  if (!Array.isArray(candidates) || !candidates.length) return null;
  return candidates.find((slot) => segmentMatchesSlot(slot, segmentLabel)) || null;
}

function walkSegmentSlotIds(segments, roots, childrenByParent) {
  const ids = [];
  let candidates = roots;

  for (const segment of segments) {
    const match = findSlotBySegmentLabel(candidates, segment);
    if (!match) {
      ids.push(null);
      candidates = [];
      continue;
    }
    ids.push(Number(match.id));
    candidates = childrenByParent.get(String(match.id)) || [];
  }

  return ids;
}

/**
 * Resolve cada segmento do caminho para o id do passo correspondente.
 * `targetSlotId` garante link no passo alvo quando o parser não encontra o último segmento.
 */
export function resolveSlotIdsFromPath(pathString, slots = [], targetSlotId = null) {
  const segments = parsePathSegments(pathString);
  if (!segments.length) return [];

  const childrenByParent = buildChildrenByParent(slots);
  const roots = childrenByParent.get("0") || [];
  const ids = walkSegmentSlotIds(segments, roots, childrenByParent);

  if (targetSlotId != null && Number.isFinite(Number(targetSlotId))) {
    const target = Number(targetSlotId);
    const slotById = new Map((slots || []).map((s) => [Number(s.id), s]));
    const canonicalPath = buildSlotPath(target, slotById);
    const canonicalSegments = parsePathSegments(canonicalPath);

    if (canonicalSegments.length === segments.length) {
      const canonicalIds = walkSegmentSlotIds(canonicalSegments, roots, childrenByParent);
      for (let i = 0; i < segments.length; i += 1) {
        if (ids[i] == null && canonicalIds[i] != null) {
          ids[i] = canonicalIds[i];
        }
      }
    }

    const lastIdx = segments.length - 1;
    if (ids[lastIdx] == null) {
      ids[lastIdx] = target;
    }
  }

  return ids;
}

export function getAncestorSlotIds(slotId, slots = []) {
  const slotById = new Map((slots || []).map((s) => [Number(s.id), s]));
  const ids = [];
  let current = slotById.get(Number(slotId));

  while (current) {
    const parentId = Number(current.id_pai) || 0;
    if (parentId <= 0) break;
    ids.push(String(parentId));
    current = slotById.get(parentId);
  }

  return ids;
}

/** Abre ancestrais na árvore para revelar o passo indicado. */
export function buildOpenNodesForSlot(slotId, slots = []) {
  const openNodes = {};
  for (const id of getAncestorSlotIds(slotId, slots)) {
    openNodes[id] = true;
  }
  return openNodes;
}

export function buildPathLinkSegments(pathString, slots = [], targetSlotId = null) {
  const segments = parsePathSegments(pathString);
  const slotIds = resolveSlotIdsFromPath(pathString, slots, targetSlotId);
  return segments.map((label, index) => ({
    label,
    slotId: slotIds[index] ?? null,
  }));
}
